/**
 * Environment-aware logger utility for the application.
 * 
 * - Development: All logs are shown
 * - Production: Only errors and warnings are shown
 * 
 * Usage:
 *   logger.debug('Detailed debug info', data)
 *   logger.info('General information', data)
 *   logger.warn('Warning message', data)
 *   logger.error('Error message', error)
 */

const isDev = import.meta.env.DEV

export interface Logger {
  debug(...args: unknown[]): void
  info(...args: unknown[]): void
  warn(...args: unknown[]): void
  error(...args: unknown[]): void
}

/**
 * Creates a logger with an optional category prefix
 */
function createLogger(category?: string): Logger {
  const prefix = category ? `[${category}]` : ''

  return {
    debug: (...args: unknown[]) => {
      if (isDev) {
        console.log(prefix, ...args)
      }
    },
    info: (...args: unknown[]) => {
      if (isDev) {
        console.info(prefix, ...args)
      }
    },
    warn: (...args: unknown[]) => {
      console.warn(prefix, ...args)
    },
    error: (...args: unknown[]) => {
      console.error(prefix, ...args)
    }
  }
}

// Default logger without category
export const logger = createLogger()

// Category-specific loggers for different parts of the app
export const apiLogger = createLogger('API')
export const authLogger = createLogger('Auth')
export const dbLogger = createLogger('DB')
export const routeLogger = createLogger('Route')

// Export the factory function for custom categories
export { createLogger }
